import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DollarSign, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { formatUSD } from '@/utils/formatters';

const PERIODS = [
  { key: 'daily', label: 'Today' },
  { key: 'weekly', label: 'This Week' },
  { key: 'monthly', label: 'This Month' },
  { key: 'yearly', label: 'This Year' },
];

const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6'];

const getDomain = (email) => {
  const parts = (email || '').split('@');
  return parts.length > 1 ? parts[1].toLowerCase() : 'unknown';
};

const TeamRevenue = () => {
  const { user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [activeDomain, setActiveDomain] = useState('all');

  useEffect(() => { loadData(); }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/team-revenue?period=${period}`);
      setData(res.data);
    } catch (e) { toast.error('Failed to load revenue data'); }
    setLoading(false);
  };

  // Extract domains from all-time data
  const domains = useMemo(() => {
    if (!data) return [];
    const domainMap = {};
    for (const r of (data.all_time || [])) {
      const domain = getDomain(r.resource);
      if (!domainMap[domain]) domainMap[domain] = { domain, count: 0, revenue: 0 };
      domainMap[domain].count++;
      domainMap[domain].revenue += r.total_revenue;
    }
    return Object.values(domainMap).sort((a, b) => b.revenue - a.revenue);
  }, [data]);

  // Filter by domain
  const allTimeFiltered = useMemo(() => {
    if (!data) return [];
    if (activeDomain === 'all') return data.all_time || [];
    return (data.all_time || []).filter(r => getDomain(r.resource) === activeDomain);
  }, [data, activeDomain]);

  const periodFiltered = useMemo(() => {
    if (!data) return [];
    if (activeDomain === 'all') return data.period_data || [];
    return (data.period_data || []).filter(r => getDomain(r.resource) === activeDomain);
  }, [data, activeDomain]);

  const filteredTotalRev = useMemo(() => allTimeFiltered.reduce((s, r) => s + r.total_revenue, 0), [allTimeFiltered]);
  const filteredPeriodRev = useMemo(() => periodFiltered.reduce((s, r) => s + r.revenue, 0), [periodFiltered]);
  const periodMax = useMemo(() => Math.max(...periodFiltered.map(r => r.revenue), 1), [periodFiltered]);

  if (loading && !data) {
    return <DashboardLayout user={user}><div className="flex items-center justify-center h-96"><div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="team-revenue-title">Team Revenue</h1>
            <p className="text-muted-foreground">Revenue contribution per resource email</p>
          </div>
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {PERIODS.map(p => (
              <Button key={p.key} variant={period === p.key ? 'default' : 'ghost'} size="sm" onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}>
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Domain Tabs */}
        <div className="flex gap-1 flex-wrap bg-muted rounded-lg p-1">
          <Button variant={activeDomain === 'all' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveDomain('all')}>
            All ({(data?.all_time || []).length})
          </Button>
          {domains.map(d => (
            <Button key={d.domain} variant={activeDomain === d.domain ? 'default' : 'ghost'} size="sm" onClick={() => setActiveDomain(d.domain)}>
              @{d.domain} ({d.count}) — {formatUSD(d.revenue)}
            </Button>
          ))}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-green-600">{formatUSD(filteredTotalRev)}</p>
              <p className="text-xs text-muted-foreground">{activeDomain === 'all' ? 'All-Time' : `@${activeDomain}`} Revenue</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-violet-600">{formatUSD(filteredPeriodRev)}</p>
              <p className="text-xs text-muted-foreground">{PERIODS.find(p => p.key === period)?.label} Revenue</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-blue-600">{allTimeFiltered.length}</p>
              <p className="text-xs text-muted-foreground">Resources</p>
            </CardContent>
          </Card>
        </div>

        {/* Period Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              {PERIODS.find(p => p.key === period)?.label} — Top Contributors
              {activeDomain !== 'all' && <Badge variant="outline" className="ml-2">@{activeDomain}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {periodFiltered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No revenue data for this period</p>
            ) : (
              <div className="space-y-3">
                {periodFiltered.slice(0, 15).map((r, i) => {
                  const pct = periodMax > 0 ? (r.revenue / periodMax) * 100 : 0;
                  return (
                    <div key={r.resource} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate max-w-[300px]">
                          {i < 3 && <span className="mr-1">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>}
                          {r.resource}
                        </span>
                        <div className="flex items-center gap-4 shrink-0 whitespace-nowrap">
                          <span className="text-muted-foreground">{r.billings} bills</span>
                          <span className="text-muted-foreground">{Math.round(r.minutes)} min</span>
                          <span className="font-semibold text-green-600">{formatUSD(r.revenue)}</span>
                        </div>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* All-Time Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-500" />
              Revenue Ranking ({allTimeFiltered.length})
              {activeDomain !== 'all' && <Badge variant="outline" className="ml-2">@{activeDomain}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead className="text-center">Resource Email</TableHead>
                    <TableHead className="text-center">Domain</TableHead>
                    <TableHead className="text-center">Total Revenue</TableHead>
                    <TableHead className="text-center">Billings</TableHead>
                    <TableHead className="text-center">Total Minutes</TableHead>
                    <TableHead className="text-center">Share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allTimeFiltered.slice(0, 30).map((r, i) => (
                    <TableRow key={r.resource}>
                      <TableCell className="text-center text-muted-foreground text-xs">
                        {i < 3 ? <span>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span> : i + 1}
                      </TableCell>
                      <TableCell className="text-center text-sm font-medium">{r.resource}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-[10px]">@{getDomain(r.resource)}</Badge>
                      </TableCell>
                      <TableCell className="text-center font-semibold text-green-600">{formatUSD(r.total_revenue)}</TableCell>
                      <TableCell className="text-center">{r.total_billings}</TableCell>
                      <TableCell className="text-center">{Math.round(r.total_minutes).toLocaleString()}</TableCell>
                      <TableCell className="text-center text-muted-foreground">
                        {filteredTotalRev > 0 ? ((r.total_revenue / filteredTotalRev) * 100).toFixed(1) : 0}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Client Office Staff (PO Created By) — Separate Section */}
        {(data?.client_office || []).length > 0 && (
          <Card className="border-l-4 border-l-orange-400">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-orange-500" />
                Client Office Staff — PO Created By ({(data.client_office || []).length})
                <Badge variant="outline" className="ml-2 text-xs">{formatUSD(data.client_office_total || 0)} total</Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">These are contacts at the client office who created POs — not your translators</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-64">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead className="text-center">Name</TableHead>
                      <TableHead className="text-center">Revenue</TableHead>
                      <TableHead className="text-center">Billings</TableHead>
                      <TableHead className="text-center">Minutes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.client_office || []).map((r, i) => (
                      <TableRow key={r.name}>
                        <TableCell className="text-center text-muted-foreground text-xs">{i + 1}</TableCell>
                        <TableCell className="text-center text-sm">{r.name}</TableCell>
                        <TableCell className="text-center text-sm text-orange-600">{formatUSD(r.total_revenue)}</TableCell>
                        <TableCell className="text-center text-sm">{r.total_billings}</TableCell>
                        <TableCell className="text-center text-sm">{Math.round(r.total_minutes).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TeamRevenue;
